import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface PlayerComparisonProps {
  players: Array<{
    name: string;
    sessions: number;
    avgQuality: number;
    improvement: number;
  }>;
  timeRange?: string;
}

export function PlayerComparison({ players }: PlayerComparisonProps) {
  if (!players?.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">No player data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Player Rankings */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Rankings</CardTitle>
            <CardDescription>
              Players ranked by average shot quality
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {players
                .sort((a, b) => b.avgQuality - a.avgQuality)
                .map((player, index) => (
                <div key={player.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-sm">
                      #{index + 1}
                    </div>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>
                        {player.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{player.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {player.sessions} sessions
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{player.avgQuality.toFixed(1)}/10</p>
                    <div className="flex items-center text-sm">
                      {player.improvement > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                      )}
                      <span className={player.improvement > 0 ? 'text-green-600' : 'text-red-600'}>
                        {player.improvement > 0 ? '+' : ''}{player.improvement}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Improvement Leaders */}
        <Card>
          <CardHeader>
            <CardTitle>Most Improved Players</CardTitle>
            <CardDescription>
              Players with highest improvement rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {players
                .sort((a, b) => b.improvement - a.improvement)
                .map((player, index) => (
                <div key={player.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 font-bold text-sm">
                      #{index + 1}
                    </div>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback>
                        {player.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{player.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Quality: {player.avgQuality.toFixed(1)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="success">
                    +{player.improvement}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance vs Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance vs Activity</CardTitle>
          <CardDescription>
            Relationship between session frequency and performance quality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart
              data={players}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="sessions" 
                type="number"
                domain={[0, 'dataMax + 2']}
                name="Sessions"
                label={{ value: 'Number of Sessions', position: 'bottom' }}
              />
              <YAxis 
                dataKey="avgQuality" 
                type="number"
                domain={[7, 10]}
                name="Quality"
                label={{ value: 'Average Quality', angle: -90, position: 'left' }}
              />
              <Tooltip
                formatter={(value: number | string, name) => [
                  name === 'avgQuality' && typeof value === 'number' ? value.toFixed(1) : value,
                  name === 'avgQuality' ? 'Avg Quality' : 'Sessions'
                ]}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
              />
              <Scatter 
                dataKey="avgQuality" 
                fill="hsl(var(--primary))"
                r={6}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Session Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Training Activity</CardTitle>
          <CardDescription>
            Number of sessions completed by each player
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={players} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip 
                formatter={(value: number) => [value, 'Sessions']}
              />
              <Bar 
                dataKey="sessions" 
                fill="hsl(var(--secondary))"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}